import { useEffect, useRef, useState } from 'react'

import type { CouponApplyResult } from 'src/Components/ScheduleSummary'
import {
  CHECKOUT_HIGHLIGHTED_OFFER_TYPE,
  CouponDiscountType,
  OfferedCoupon,
  useGetOfferedCouponsQuery,
} from 'src/Redux/artistCouponsApi'
import {
  OrderItemType,
  type OrderCheckoutInfoResponse,
} from 'src/Redux/artistOrderApi'

export const MINIMUM_SPEND_ERROR =
  'Minimum spend requirement not met for coupon'

export const OFFER_WINDOW_LOWER_SHARE = 0.5

const DEFAULT_COUPON_TARGET_TYPES: OrderItemType[] = [
  OrderItemType.SpotifyCampaign,
  OrderItemType.TiktokCampaign,
]

export enum ThresholdCouponState {
  None = 'none',
  Offered = 'offered',
  AttachedBelowThreshold = 'attached_below_threshold',
  Unlocked = 'unlocked',
}

type BudgetRange = {
  min?: number
  max?: number
}

export type UseThresholdCouponParams = {
  appliedCoupon: string
  checkoutInfo?: OrderCheckoutInfoResponse | null
  budget: number
  budgetRange?: BudgetRange
  isManualMatching: boolean
  isOrderFullyCovered?: boolean
  isOrderMutable?: boolean
  onApplyPromocode: (
    couponCode: string,
  ) => Promise<CouponApplyResult | undefined>
  onRemoveCoupon: () => void
  applyBudget: (budget: number) => Promise<void> | void
}

export type UseThresholdCoupon = {
  state: ThresholdCouponState
  offer: OfferedCoupon | null
  couponCode: string
  discountPercent: number | null
  threshold: number
  eligibleTotal: number
  remaining: number
  progress: number
  canRaise: boolean
  raiseTargetBudget: number | null
  raisePayable: number | null
  onApplyOffer: () => Promise<CouponApplyResult | undefined>
  onRemoveCoupon: () => Promise<void>
  onRaise: () => Promise<void>
}

type MinimumSpendError = {
  threshold: number
}

export const findMinimumSpendError = (
  checkoutInfo: OrderCheckoutInfoResponse | null | undefined,
  couponCode: string,
): MinimumSpendError | null => {
  if (!checkoutInfo || !couponCode) {
    return null
  }

  const topLevelError = checkoutInfo.couponErrors.find(
    (item) =>
      item.couponCode === couponCode && item.error === MINIMUM_SPEND_ERROR,
  )
  const itemError = checkoutInfo.items
    .flatMap((item) => item.discounts)
    .find(
      (discount) =>
        discount.couponCode === couponCode &&
        discount.error === MINIMUM_SPEND_ERROR,
    )

  const threshold = Number(topLevelError?.param ?? itemError?.param)

  return topLevelError || itemError
    ? { threshold: Number.isFinite(threshold) ? threshold : 0 }
    : null
}

export const getCouponToKeep = (
  error: { message: string } | null | undefined,
  couponCode: string,
): string => (!error || error.message === MINIMUM_SPEND_ERROR ? couponCode : '')

export const getPayableCoupon = (
  checkoutInfo: OrderCheckoutInfoResponse | null | undefined,
  appliedCoupon: string,
): string =>
  findMinimumSpendError(checkoutInfo, appliedCoupon) ? '' : appliedCoupon

const isHighlightedPercentageCoupon = (coupon: OfferedCoupon): boolean =>
  coupon.offerType === CHECKOUT_HIGHLIGHTED_OFFER_TYPE &&
  coupon.discountType === CouponDiscountType.Percentage &&
  coupon.minSpend > 0

const getEligibleTotal = (
  checkoutInfo: OrderCheckoutInfoResponse | null | undefined,
  targetTypes: OfferedCoupon['targetOrderItemTypes'] | undefined,
): number => {
  const eligibleTypes = targetTypes?.length
    ? targetTypes
    : DEFAULT_COUPON_TARGET_TYPES

  return (checkoutInfo?.items ?? [])
    .filter((item) => eligibleTypes.includes(item.type))
    .reduce((sum, item) => sum + item.fullPrice, 0)
}

const isInsideOfferWindow = (offer: OfferedCoupon, total: number): boolean =>
  total > offer.minSpend * OFFER_WINDOW_LOWER_SHARE &&
  total < offer.minSpend * (1 - offer.discount / 100)

const CENTS = 100

const getRaisePayable = (
  raiseTargetBudget: number | null,
  discountPercent: number | null,
): number | null =>
  raiseTargetBudget === null || discountPercent === null
    ? null
    : Math.round(raiseTargetBudget * (1 - discountPercent / 100) * CENTS) /
      CENTS

const getRaiseTarget = (
  budget: number,
  remaining: number,
  range?: BudgetRange,
): number | null => {
  if (remaining <= 0 || typeof range?.max !== 'number') {
    return null
  }

  const target = budget + remaining

  if (target > range.max) {
    return null
  }

  return Math.max(target, range.min ?? 0)
}

type ResolveParams = {
  offered: OfferedCoupon | null
  appliedCoupon: string
  checkoutInfo?: OrderCheckoutInfoResponse | null
  knownThresholds: Record<string, number>
  rejectedOfferCode: string
  budget: number
  budgetRange?: BudgetRange
  isManualMatching: boolean
  isOrderFullyCovered: boolean
  isOrderMutable: boolean
}

type ResolvedThresholdCoupon = Omit<
  UseThresholdCoupon,
  'onApplyOffer' | 'onRemoveCoupon' | 'onRaise'
>

const getAppliedThreshold = (
  offered: OfferedCoupon | null,
  appliedCoupon: string,
  errorThreshold: number | undefined,
  knownThresholds: Record<string, number>,
): number => {
  if (errorThreshold) {
    return errorThreshold
  }
  if (offered?.code === appliedCoupon) {
    return offered.minSpend
  }
  return knownThresholds[appliedCoupon] ?? 0
}

const getAppliedState = (
  threshold: number,
  hasMinimumSpendError: boolean,
): ThresholdCouponState => {
  if (!threshold) {
    return ThresholdCouponState.None
  }
  return hasMinimumSpendError
    ? ThresholdCouponState.AttachedBelowThreshold
    : ThresholdCouponState.Unlocked
}

const getOfferVisibility = ({
  offered,
  rejectedOfferCode,
  isManualMatching,
  isOrderFullyCovered,
  budgetRange,
  eligibleTotal,
}: Pick<
  ResolveParams,
  | 'offered'
  | 'rejectedOfferCode'
  | 'isManualMatching'
  | 'isOrderFullyCovered'
  | 'budgetRange'
> & { eligibleTotal: number }): boolean => {
  if (!offered || isManualMatching || isOrderFullyCovered) {
    return false
  }
  if (
    typeof budgetRange?.max === 'number' &&
    offered.minSpend > budgetRange.max
  ) {
    return false
  }
  return (
    rejectedOfferCode !== offered.code &&
    isInsideOfferWindow(offered, eligibleTotal)
  )
}

type BuildResolvedParams = Pick<
  ResolveParams,
  'budget' | 'budgetRange' | 'isManualMatching' | 'isOrderMutable'
> & {
  state: ThresholdCouponState
  offer: OfferedCoupon | null
  couponCode: string
  discountPercent: number | null
  threshold: number
  eligibleTotal: number
}

const buildResolved = (
  params: BuildResolvedParams,
): ResolvedThresholdCoupon => {
  const { state, offer, couponCode, discountPercent, eligibleTotal } = params
  const isTracking = state !== ThresholdCouponState.None
  const threshold = isTracking ? params.threshold : 0
  const remaining = Math.max(0, threshold - eligibleTotal)
  const progress =
    threshold > 0 ? Math.min(100, (eligibleTotal / threshold) * 100) : 0
  const raiseTargetBudget =
    isTracking && !params.isManualMatching && params.isOrderMutable
      ? getRaiseTarget(params.budget, remaining, params.budgetRange)
      : null

  return {
    canRaise: raiseTargetBudget !== null,
    raisePayable: getRaisePayable(raiseTargetBudget, discountPercent),
    offer,
    couponCode,
    discountPercent,
    state,
    threshold,
    eligibleTotal,
    remaining,
    progress,
    raiseTargetBudget,
  }
}

const resolveIdleCoupon = (params: ResolveParams): ResolvedThresholdCoupon => {
  const { offered, checkoutInfo } = params
  const eligibleTotal = getEligibleTotal(
    checkoutInfo,
    offered?.targetOrderItemTypes,
  )
  const isVisible = getOfferVisibility({ ...params, eligibleTotal })

  return buildResolved({
    ...params,
    state: getIdleState(isVisible),
    offer: isVisible ? offered : null,
    couponCode: getOfferedCode(offered, isVisible),
    discountPercent: offered?.discount ?? null,
    threshold: offered?.minSpend ?? 0,
    eligibleTotal,
  })
}

const resolveAppliedCoupon = (
  params: ResolveParams,
): ResolvedThresholdCoupon => {
  const { offered, appliedCoupon, checkoutInfo } = params
  const appliedOffer = offered?.code === appliedCoupon ? offered : null
  const minimumSpendError = findMinimumSpendError(checkoutInfo, appliedCoupon)
  const threshold = getAppliedThreshold(
    offered,
    appliedCoupon,
    minimumSpendError?.threshold,
    params.knownThresholds,
  )

  return buildResolved({
    ...params,
    state: getAppliedState(threshold, Boolean(minimumSpendError)),
    offer: null,
    couponCode: appliedCoupon,
    discountPercent: appliedOffer?.discount ?? null,
    threshold,
    eligibleTotal: getEligibleTotal(
      checkoutInfo,
      appliedOffer?.targetOrderItemTypes,
    ),
  })
}

const resolveThresholdCoupon = (
  params: ResolveParams,
): ResolvedThresholdCoupon =>
  params.appliedCoupon
    ? resolveAppliedCoupon(params)
    : resolveIdleCoupon(params)

const getIdleState = (isOfferVisible: boolean): ThresholdCouponState =>
  isOfferVisible ? ThresholdCouponState.Offered : ThresholdCouponState.None

const getOfferedCode = (
  offered: OfferedCoupon | null,
  isOfferVisible: boolean,
): string => (isOfferVisible ? (offered?.code ?? '') : '')

export const useThresholdCoupon = ({
  appliedCoupon,
  checkoutInfo,
  budget,
  budgetRange,
  isManualMatching,
  isOrderFullyCovered = false,
  isOrderMutable = true,
  onApplyPromocode,
  onRemoveCoupon,
  applyBudget,
}: UseThresholdCouponParams): UseThresholdCoupon => {
  const { data } = useGetOfferedCouponsQuery()

  const [knownThresholds, setKnownThresholds] = useState<
    Record<string, number>
  >({})
  const [rejectedOfferCode, setRejectedOfferCode] = useState('')
  const budgetBeforeRaiseRef = useRef<number | null>(null)
  const raisedBudgetRef = useRef<number | null>(null)

  const offered = data?.coupons.find(isHighlightedPercentageCoupon) ?? null

  const errorThreshold = findMinimumSpendError(
    checkoutInfo,
    appliedCoupon,
  )?.threshold

  if (
    appliedCoupon &&
    errorThreshold &&
    knownThresholds[appliedCoupon] !== errorThreshold
  ) {
    setKnownThresholds({ ...knownThresholds, [appliedCoupon]: errorThreshold })
  }

  useEffect(() => {
    const previous = budgetBeforeRaiseRef.current
    const raised = raisedBudgetRef.current
    if (previous === null || raised === null) {
      return
    }
    if (budget !== previous && budget !== raised) {
      budgetBeforeRaiseRef.current = null
      raisedBudgetRef.current = null
    }
  }, [budget])

  const resolved = resolveThresholdCoupon({
    offered,
    appliedCoupon,
    checkoutInfo,
    knownThresholds,
    rejectedOfferCode,
    budget,
    budgetRange,
    isManualMatching,
    isOrderFullyCovered,
    isOrderMutable,
  })

  const handleApplyOffer = async (): Promise<CouponApplyResult | undefined> => {
    if (!offered) {
      return void 0
    }
    const result = await onApplyPromocode(offered.code)
    if (
      result?.coupon.error &&
      result.coupon.error.message !== MINIMUM_SPEND_ERROR
    ) {
      setRejectedOfferCode(offered.code)
    }
    return result
  }

  const handleRaise = async (): Promise<void> => {
    if (resolved.raiseTargetBudget === null) {
      return
    }
    if (budgetBeforeRaiseRef.current === null) {
      budgetBeforeRaiseRef.current = budget
    }
    raisedBudgetRef.current = resolved.raiseTargetBudget
    await applyBudget(resolved.raiseTargetBudget)
  }

  const handleRemoveCoupon = async (): Promise<void> => {
    const previousBudget = budgetBeforeRaiseRef.current
    budgetBeforeRaiseRef.current = null
    raisedBudgetRef.current = null
    setKnownThresholds((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([code]) => code !== appliedCoupon),
      ),
    )
    setRejectedOfferCode('')
    onRemoveCoupon()
    if (previousBudget !== null) {
      await applyBudget(previousBudget)
    }
  }

  return {
    ...resolved,
    onApplyOffer: handleApplyOffer,
    onRemoveCoupon: handleRemoveCoupon,
    onRaise: handleRaise,
  }
}
